import { BaseControl, IControlParameters } from '@Common/Controls/BaseControl';
import { Button, IButtonParameters } from '@Common/Controls/SimpleButton';
import { FilterType } from '@Common/Enums';
import StylesHelper from '@Common/Helpers/StylesHelper';
import { SimpleButtonAppearance } from '@InstanceFactory';
import { SearchControl } from './Search';
import urlHelper from '@Common/Helpers/UrlHelper';
import { safeDestroys } from '@Common/Core/Destroyable';

export interface IActionButtonsParameters extends IControlParameters {
    searchPanel?: boolean;
    onSearch?: (data: any) => void;
    adjust?: () => void;
    searchToggle?: (searchState: boolean) => void;
    resizable?: boolean;
    buttons?: any[];

    actionButtonsAppearance?: SimpleButtonAppearance;
    actionPanelStyle?: string;
    searchImageStyle?: string;
    stylesHelper: StylesHelper;
}

export class ActionButtons extends BaseControl<IActionButtonsParameters> {
    private searchInput: SearchControl;
    private searchControl: JQuery<HTMLElement>;
    private searchVisible: boolean;
    private searchContainer: JQuery<HTMLElement>;

    private readonly buttonMargin = '5px';

    private readonly buttonStyle = null;

    public readonly buttons: Button[] = [];

    constructor(param: IActionButtonsParameters) {
        super(param);

        this._param = $.extend(
            {
                css: null,
                className: '',
                width: 'auto',
                height: 'auto',

                searchPanel: false,
                onSearch: $.noop,
                adjust: $.noop,
                searchToggle: $.noop,
                resizable: false,
                buttons: [],

                actionButtonsAppearance: null,
                actionPanelStyle: null,
                searchImageStyle: null,
                stylesHelper: null
            },
            param
        );

        if (param.actionButtonsAppearance) {
            this.buttonStyle = {
                normal: param.stylesHelper.mergeStyles(param.actionButtonsAppearance.textStyleName, param.actionButtonsAppearance.borderStyleName),
                hover: param.stylesHelper.mergeStyles(
                    param.actionButtonsAppearance.hoverTextStyleName,
                    param.actionButtonsAppearance.hoverBorderStyleName
                )
            };
        }

        this._init();
    }

    protected _init() {
        this._root = $('<div class="action-buttons"/>').css({
            width: '100%',
            minWidth: '140px',
            textAlign: 'right'
        });

        if (this._param.actionPanelStyle) {
            this._root.addClass(this._param.stylesHelper.getStyleName(this._param.actionPanelStyle));
        }

        if (this._param.searchPanel) {
            this.searchInput = new SearchControl({
                width: '100%'
            });
            this.searchInput.onChange(() => {
                this.search();
            });
            this.searchControl = $('<div />')
                .css({
                    padding: '5px'
                })
                .append(this.searchInput.getControl());
        }

        const style = this.buttonStyle;

        _(this._param.buttons).forEach((button, index) => {
            const buttonControl = new Button({
                text: button.text,
                html: button.html,
                height: '30px',
                width: button.searchButton ? '30px' : 'auto',
                fontSize: '14px',
                icon: button.icon,
                css:
                    style &&
                    (button.searchButton
                        ? this.overrideUnderline(this._param.stylesHelper.mergeStyles(style.normal, this._param.searchImageStyle, true))
                        : style.normal),
                hoverCss:
                    style &&
                    (button.searchButton
                        ? this.overrideUnderline(this._param.stylesHelper.mergeStyles(style.hover, this._param.searchImageStyle, true))
                        : style.hover)
            } as IButtonParameters);

            const buttonContainer = $('<div />').css({ display: 'inline-block' }).append(buttonControl.getControl());
            buttonContainer.css({
                'padding-top': this.buttonMargin,
                'margin-bottom': this.buttonMargin,
                'margin-right': this.buttonMargin
            });
            if (index === 0) {
                buttonContainer.css('margin-left', '10px');
            }
            if (this._param.resizable && index === this._param.buttons.length - 1) {
                buttonContainer.css({ 'margin-right': '10px' });
            }
            this._root.append(buttonContainer);
            let callback = button.click;
            if (button.searchButton) {
                this.searchContainer = buttonContainer;
                this.searchContainer.addClass('search-container');
                callback = () => {
                    this.searchVisible = !this.searchVisible;
                    this.setSearchVisibility();
                    this._param.adjust();
                };
            }
            buttonControl.onClick(callback);
            this.buttons.push(buttonControl);
        });

        this.setSearchVisibility();
    }

    private setSearchVisibility(): void {
        if (this.searchControl) {
            if (this.searchVisible) {
                this._root.prepend(this.searchControl);
                this.searchContainer.css({
                    'background-image': urlHelper.getCssImageUrl('CommonElements/arrow-up.png'),
                    'background-position': 'top',
                    'background-repeat': 'no-repeat'
                });

                this.searchInput.getControl().trigger('focus');
            } else {
                this.searchControl.detach();
                this.searchContainer.css({
                    background: 'none'
                });
            }

            this._param.searchToggle(!this.searchVisible);
        }
    }

    private search(): void {
        const value = this.searchInput.getValue();
        this._param.onSearch({
            operatorType: FilterType.includes,
            values: [value]
        });
    }

    private overrideUnderline(style: any): any {
        style.textDecoration = 'none';
        return style;
    }

    public destroy(): void {
        safeDestroys(...this.buttons);
        super.destroy();
    }
}
